import { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const GalleryDetail = () => {
  const { galleryId, slug } = useParams();
  const galleryIdentifier = slug || galleryId;
  const { user } = useContext(AuthContext);
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [totalUploads, setTotalUploads] = useState(0);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordStatus, setPasswordStatus] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState({
    total: 0,
    last_attempt_at: null,
  });
  const [unlockedPassword, setUnlockedPassword] = useState("");
  const [nextImageCursor, setNextImageCursor] = useState(null);
  const [hasMoreImages, setHasMoreImages] = useState(false);
  const [loadingMoreImages, setLoadingMoreImages] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editCategoryId, setEditCategoryId] = useState("");
  const isGalleryOwner = useMemo(
    () =>
      Boolean(
        user?.id &&
          gallery?.user_id &&
          String(user.id) === String(gallery.user_id)
      ),
    [user, gallery]
  );
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  const [isPublic, setIsPublic] = useState(false);
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const ALLOWED_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"]);
  const ALLOWED_EXTS = new Set([".jpg", ".jpeg", ".png"]);

  const isAllowedFile = (file) => {
    const type = (file.type || "").toLowerCase();
    if (ALLOWED_TYPES.has(type) || type.startsWith("image/")) return true;
    const name = (file.name || "").toLowerCase();
    return Array.from(ALLOWED_EXTS).some((ext) => name.endsWith(ext));
  };

  const readAllEntries = (dirReader) =>
    new Promise((resolve, reject) => {
      const entries = [];
      const readBatch = () => {
        dirReader.readEntries(
          (batch) => {
            if (!batch.length) {
              resolve(entries);
              return;
            }
            entries.push(...batch);
            readBatch();
          },
          (err) => reject(err)
        );
      };
      readBatch();
    });

  const traverseFileTree = async (entry) => {
    if (entry.isFile) {
      return new Promise((resolve, reject) =>
        entry.file((file) => resolve([file]), reject)
      );
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const entries = await readAllEntries(reader);
      const nested = await Promise.all(entries.map(traverseFileTree));
      return nested.flat();
    }
    return [];
  };

  const getFilesFromDataTransfer = async (dataTransfer) => {
    if (dataTransfer.items && dataTransfer.items.length) {
      const collected = await Promise.all(
        Array.from(dataTransfer.items).map((item) => {
          const entry = item.webkitGetAsEntry?.();
          if (entry) return traverseFileTree(entry);
          const file = item.getAsFile?.();
          return file ? Promise.resolve([file]) : Promise.resolve([]);
        })
      );
      return collected.flat();
    }
    return Array.from(dataTransfer.files || []);
  };


  const fetchGalleryImages = useCallback(async (overridePassword, cursor = null) => {
    try {
      if (cursor) setLoadingMoreImages(true);
      const storedPassword = overridePassword ?? unlockedPassword;
      const token = localStorage.getItem("token");
      const requestHeaders = {
        ...(storedPassword ? { "X-Gallery-Password": storedPassword } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const imagesRes = await axios.get(
        API_URL + `/api/galleries/${galleryIdentifier}/images`,
        {
          params: { limit: 60, ...(cursor ? { cursor } : {}) },
          headers: requestHeaders,
        }
      );
      const pageImages = imagesRes.data.images || [];
      setImages((current) => cursor ? [...current, ...pageImages] : pageImages);
      setHasMoreImages(Boolean(imagesRes.data.hasMore));
      setNextImageCursor(imagesRes.data.nextCursor || null);
    } catch (err) {
      if (
        err.response?.status === 401 &&
        err.response?.data?.requiresPassword
      ) {
        setRequiresPassword(true);
        return;
      }
      console.error(
        "Error fetching images:",
        err.response?.data || err.message
      );
    } finally {
      setLoadingMoreImages(false);
    }
  }, [galleryIdentifier, API_URL, unlockedPassword]);

  useEffect(() => {
    const fetchGalleryData = async (overridePassword) => {
      try {
        const token = localStorage.getItem("token");
        const storedPassword = overridePassword ?? unlockedPassword;
        const galleryRes = await axios.get(
          API_URL + `/api/galleries/${galleryIdentifier}`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(storedPassword ? { "X-Gallery-Password": storedPassword } : {}),
            },
          }
        );
        setGallery(galleryRes.data.gallery);
        setIsPublic(galleryRes.data.gallery.is_public);
        setEditTitle(galleryRes.data.gallery.title || "");
        setEditDescription(galleryRes.data.gallery.description || "");
        setEditCategoryId(galleryRes.data.gallery.category_id || "");
        setPasswordStatus(Boolean(galleryRes.data.gallery.has_password));
        fetchGalleryImages(storedPassword);
      } catch (err) {
        console.error(
          "Error fetching gallery:",
          err.response?.data || err.message
        );
        if (err.response?.status === 404) {
          setNotFound(true);
          setError("Gallery not found.");
        } else if (
          err.response?.status === 401 &&
          err.response?.data?.requiresPassword
        ) {
          setRequiresPassword(true);
          setError("");
        } else {
          setError("Failed to load gallery.");
        }
      }
    };

    setNotFound(false);
    fetchGalleryData();
  }, [galleryIdentifier, fetchGalleryImages, unlockedPassword, API_URL]);

  useEffect(() => {
    if (!isGalleryOwner) return;
    const token = localStorage.getItem("token");
    axios.get(`${API_URL}/api/categories`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => setCategories(response.data.categories || []))
      .catch(() => setError("Categories could not be loaded."));
  }, [API_URL, isGalleryOwner]);

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragging(false);

    const files = await getFilesFromDataTransfer(event.dataTransfer);

    if (files.length > 0) {
      handleMultipleImageUpload(files);
    }
  };

  const galleryGridRef = useRef(null);
  const [gridColumns, setGridColumns] = useState(4);
  const [visibleRowRange, setVisibleRowRange] = useState({ start: 0, end: 0 });
  const rowHeight = 320;
  const rowGap = 12;
  const rowStride = rowHeight + rowGap;
  const overscanRows = 2;

  useEffect(() => {
    const updateColumns = () => {
      const width = galleryGridRef.current?.clientWidth || window.innerWidth;
      if (width < 640) {
        setGridColumns(1);
      } else if (width < 900) {
        setGridColumns(2);
      } else if (width < 1200) {
        setGridColumns(3);
      } else {
        setGridColumns(4);
      }
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  useEffect(() => {
    const updateVisibleRows = () => {
      if (!galleryGridRef.current) return;
      const rect = galleryGridRef.current.getBoundingClientRect();
      const totalRows = Math.ceil(images.length / gridColumns);
      const viewportTop = 0;
      const viewportBottom = window.innerHeight;
      const scrolledIntoGrid = Math.max(0, viewportTop - rect.top);
      const scrolledPastGrid = Math.max(0, viewportBottom - rect.top);

      const start = Math.max(
        0,
        Math.floor(scrolledIntoGrid / rowStride) - overscanRows
      );
      const end = Math.min(
        totalRows,
        Math.ceil(scrolledPastGrid / rowStride) + overscanRows
      );

      setVisibleRowRange({ start, end: Math.max(end, start + 1) });
    };

    updateVisibleRows();
    window.addEventListener("scroll", updateVisibleRows, { passive: true });
    window.addEventListener("resize", updateVisibleRows);
    return () => {
      window.removeEventListener("scroll", updateVisibleRows);
      window.removeEventListener("resize", updateVisibleRows);
    };
  }, [images.length, gridColumns, rowStride]);

  const totalRows = Math.ceil(images.length / gridColumns);
  const startIndex = visibleRowRange.start * gridColumns;
  const endIndex = Math.min(images.length, visibleRowRange.end * gridColumns);
  const visibleImages = images.slice(startIndex, endIndex);
  const topSpacerHeight = visibleRowRange.start * rowStride;
  const bottomSpacerHeight = Math.max(0, (totalRows - visibleRowRange.end) * rowStride);

  const handleMultipleImageUpload = async (files) => {
    const validFiles = files.filter(isAllowedFile);

    if (validFiles.length === 0) {
      setError(
        "No valid images found. Only JPG, JPEG, and PNG are allowed."
      );
      return;
    }

    setUploading(true);
    setUploadCount(0);
    setTotalUploads(validFiles.length);
    setUploadProgress(0);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const batchSize = 3;
      let uploaded = 0;

      for (let i = 0; i < validFiles.length; i += batchSize) {
        const batch = validFiles.slice(i, i + batchSize);

        const uploadPromises = batch.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("galleryId", galleryIdentifier);

          return axios.post(API_URL + "/api/upload", formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              setUploadProgress(progress);
            },
          });
        });

        await Promise.all(uploadPromises);
        uploaded += batch.length;

        setUploadProgress(Math.round((uploaded / validFiles.length) * 100));
        setUploadCount(uploaded);

        await fetchGalleryImages();
      }

      setUploadProgress(100);
    } catch (err) {
      console.error(
        "Error uploading images:",
        err.response?.data || err.message
      );
      setError("Failed to upload some images.");
    } finally {
      setUploading(false);
    }
  };

  const toggleGalleryPrivacy = async () => {
    try {
      const token = localStorage.getItem("token");
      const newPrivacyStatus = !isPublic;

      await axios.post(
        API_URL + `/api/galleries/toggle-privacy`,
        { galleryId: galleryIdentifier, isPublic: newPrivacyStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setIsPublic(newPrivacyStatus);
    } catch (err) {
      console.error(
        "Error updating gallery privacy:",
        err.response?.data || err.message
      );
      setError("Failed to update privacy settings.");
    }
  };

  const toggleAllowDownloads = async () => {
    try {
      const token = localStorage.getItem("token");
      const updated = !gallery.allow_downloads;

      await axios.patch(
        `${API_URL}/api/galleries/${galleryIdentifier}`,
        { allow_downloads: updated },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGallery((prev) => ({ ...prev, allow_downloads: updated }));
    } catch (err) {
      console.error(
        "Error toggling download permission:",
        err.response?.data || err.message
      );
      setError("Failed to update download permission.");
    }
  };

  const handleSaveDetails = async () => {
    try {
      setSavingDetails(true);
      const token = localStorage.getItem("token");
      const payload = {
        title: editTitle.trim(),
        description: editDescription.trim(),
        category_id: editCategoryId || null,
      };
      await axios.patch(`${API_URL}/api/galleries/${galleryIdentifier}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGallery((prev) => ({
        ...prev,
        title: payload.title,
        description: payload.description,
        category_id: payload.category_id,
      }));
      setEditingDetails(false);
    } catch (err) {
      console.error(
        "Error updating gallery details:",
        err.response?.data || err.message
      );
      setError("Failed to update gallery details.");
    } finally {
      setSavingDetails(false);
    }
  };

  const handleSetCover = async (imageId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/galleries/${galleryIdentifier}/cover`,
        { imageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGallery((prev) => ({ ...prev, cover_image: imageId }));
    } catch (err) {
      console.error(
        "Error setting cover image:",
        err.response?.data || err.message
      );
      setError("Failed to set cover image.");
    }
  };

  const handleToggleFeatured = async (imageId) => {
    const current = images.find((image) => image.id === imageId);
    if (!current) return;
    const featured = !current.is_featured;
    setImages((items) => items.map((image) => (
      image.id === imageId ? { ...image, is_featured: featured } : image
    )));
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_URL}/api/images/${imageId}/featured`,
        { featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setImages((items) => items.map((image) => (
        image.id === imageId ? { ...image, is_featured: !featured } : image
      )));
      setError("Failed to update the featured photo.");
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!isGalleryOwner) {
      setError("Only the gallery owner can delete images.");
      return;
    }

    const confirmDelete = window.confirm("Delete this image from the gallery?");
    if (!confirmDelete) return;

    try {
      setDeletingImageId(imageId);
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setImages((prev) => prev.filter((img) => img.id !== imageId));
      if (gallery.cover_image === imageId) {
        setGallery((prev) => ({ ...prev, cover_image: null }));
      }
    } catch (err) {
      console.error(
        "Error deleting image:",
        err.response?.data || err.message
      );
      setError("Failed to delete image.");
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleDownload = async (imageId) => {
    try {
      const storedPassword = unlockedPassword;
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/images/download/${imageId}`,
        {},
        {
          headers: {
            ...(storedPassword ? { "X-Gallery-Password": storedPassword } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      const { url } = res.data;

      const imageRes = await fetch(url);
      const blob = await imageRes.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `photo-${imageId}.jpg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error(
        "Error downloading image:",
        err.response?.data || err.message
      );
      setError("Failed to download image.");
    }
  };

  const handleShare = async (imageId) => {
    try {
      const shareUrl = `https://imagedelivery.net/Z1o5jP3Z0wrLOM-5XDvR8A/${imageId}/Heroplus`;
      const response = await fetch(shareUrl);
      const blob = await response.blob();
      const file = new File(
        [blob],
        `photo-${imageId}.jpg`,
        { type: blob.type || "image/jpeg" }
      );

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: gallery?.title || "Photo",
          text: "Check out this photo",
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: gallery?.title || "Photo",
          url: shareUrl,
        });
        return;
      }

      await handleDownload(imageId);
    } catch (err) {
      console.error("Error sharing image:", err);
      setError("Failed to share image.");
    }
  };

  const fetchPasswordAttempts = useCallback(async () => {
    if (!isGalleryOwner) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_URL}/api/galleries/${galleryIdentifier}/password/attempts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFailedAttempts({
        total: res.data.total || 0,
        last_attempt_at: res.data.last_attempt_at || null,
      });
    } catch (err) {
      console.error(
        "Error fetching password attempts:",
        err.response?.data || err.message
      );
    }
  }, [API_URL, galleryIdentifier, isGalleryOwner]);

  useEffect(() => {
    if (isGalleryOwner) {
      fetchPasswordAttempts();
    }
  }, [isGalleryOwner, fetchPasswordAttempts, galleryIdentifier]);

  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    try {
      await axios.post(
        `${API_URL}/api/galleries/${galleryIdentifier}/password/verify`,
        { password: passwordInput }
      );
      setUnlockedPassword(passwordInput);
      setRequiresPassword(false);
      setPasswordInput("");
      await fetchGalleryImages(passwordInput);
      const token = localStorage.getItem("token");
      const galleryRes = await axios.get(
        API_URL + `/api/galleries/${galleryIdentifier}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "X-Gallery-Password": passwordInput,
          },
        }
      );
      setGallery(galleryRes.data.gallery);
      setIsPublic(galleryRes.data.gallery.is_public);
      setEditTitle(galleryRes.data.gallery.title || "");
      setEditDescription(galleryRes.data.gallery.description || "");
      setPasswordStatus(Boolean(galleryRes.data.gallery.has_password));
    } catch (err) {
      setPasswordError(
        err.response?.data?.error || "Incorrect password. Please try again."
      );
    }
  };

  const handleSavePasswordSetting = async (newValue) => {
    try {
      setPasswordSaving(true);
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_URL}/api/galleries/${galleryIdentifier}`,
        { password: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordStatus(Boolean(newValue));
      if (!newValue) {
        setUnlockedPassword("");
      }
      setPasswordInput("");
      fetchPasswordAttempts();
    } catch (err) {
      setError("Failed to update password.");
    } finally {
      setPasswordSaving(false);
    }
  };

  if (notFound) {
    return (
      <div className="w-full mx-auto max-w-6xl px-4 sm:px-6 my-24">
        <p className="text-center text-sm sm:text-base">Gallery not found.</p>
      </div>
    );
  }

  if (requiresPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="w-full max-w-md bg-white shadow-lg p-6 rounded">
          <h2 className="text-xl font-semibold mb-3 text-center">This gallery is password protected</h2>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Enter the password to view the gallery.
          </p>
          <form onSubmit={handleVerifyPassword} className="space-y-3">
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Password"
              required
            />
            {passwordError && (
              <p className="text-red-500 text-sm text-center">{passwordError}</p>
            )}
            <button
              type="submit"
              className="bg-gray-800 text-white px-4 py-2 rounded w-full"
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto max-w-6xl px-4 sm:px-6 my-24">
      {error && <p className="text-red-500 text-sm sm:text-base">{error}</p>}

      {gallery ? (
        <>
          {isGalleryOwner ? (
            <Link
              to="/Dashboard"
              className="text-gray-800 hover:underline text-sm sm:text-base"
            >
              Back to Dashboard
            </Link>
          ) : (
            <Link
              to="/Galleries"
              className="text-gray-800 hover:underline text-sm sm:text-base"
            >
              Back to Collections
            </Link>
          )}

          {isGalleryOwner && (
            <div className="mt-4 bg-gray-100 p-4 sm:p-6 shadow-md">
              <div className="flex gap-2 mb-4">
                {[
                  { key: "upload", label: "Upload" },
                  { key: "details", label: "Gallery Details" },
                  { key: "cover", label: "Cover Image" },
                  { key: "featured", label: "Featured Photos" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      activeTab === tab.key
                        ? "bg-gray-800 text-white"
                        : "bg-white text-gray-800 border border-gray-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "upload" && (
                <div>
                  <div
                    className={`border-2 border-dashed p-4 sm:p-6 text-center ${
                      dragging ? "border-gray-500" : "border-gray-400"
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                  >
                    {dragging ? (
                      <p className="text-gray-500 text-sm sm:text-base">
                        Drop your images here...
                      </p>
                    ) : (
                      <p className="text-sm sm:text-base">
                        Drag & Drop folders or images, or click to upload
                      </p>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      multiple
                      webkitdirectory="true"
                      directory=""
                      onChange={(e) =>
                        handleMultipleImageUpload([...e.target.files])
                      }
                      disabled={uploading}
                      className="hidden"
                    />
                  </div>

                  {uploading && (
                    <div className="mt-2">
                      <p className="text-gray-800 text-sm">
                        Uploading: {uploadCount}/{totalUploads}
                      </p>
                      <div className="w-full bg-gray-200 ">
                        <div
                          className="bg-gray-800 text-xs leading-none py-1 text-center text-white"
                          style={{ width: `${uploadProgress}%` }}
                        >
                          {uploadProgress}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "details" && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={toggleGalleryPrivacy}
                      className={`px-4 py-2 text-sm sm:text-base rounded-md text-white ${
                        isPublic
                          ? "bg-green-500 hover:bg-green-600"
                          : "bg-gray-500 hover:bg-gray-600"
                      }`}
                    >
                      {isPublic ? "Public" : "Private"}
                    </button>

                    <button
                      onClick={toggleAllowDownloads}
                      className={`px-4 py-2 text-sm sm:text-base rounded-md text-white ${
                        gallery?.allow_downloads
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-gray-500 hover:bg-gray-600"
                      }`}
                    >
                      {gallery?.allow_downloads
                        ? "Downloads Enabled"
                        : "Downloads Disabled"}
                    </button>
                  </div>

                <div className="border-t border-gray-300 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Gallery Details</h3>
                    {!editingDetails && (
                        <button
                          onClick={() => setEditingDetails(true)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingDetails ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Photography category
                          </label>
                          <select
                            value={editCategoryId}
                            onChange={(e) => setEditCategoryId(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                          >
                            <option value="">Uncategorized (dashboard only)</option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Title
                          </label>
                          <input
                            type="text"
                            maxLength={40}
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            maxLength={256}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full border rounded px-3 py-2"
                            rows="3"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveDetails}
                            disabled={savingDetails || !editTitle.trim()}
                            className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
                          >
                            {savingDetails ? "Saving..." : "Save"}
                          </button>
                          <button
                            onClick={() => {
                              setEditingDetails(false);
                              setEditTitle(gallery.title || "");
                              setEditDescription(gallery.description || "");
                              setEditCategoryId(gallery.category_id || "");
                            }}
                            className="bg-gray-200 text-gray-800 px-4 py-2 rounded"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1 text-sm text-gray-800">
                        <p className="font-semibold">{gallery.title}</p>
                        <p className="text-gray-700">
                          {gallery.description || "No description"}
                        </p>
                        <p className="text-gray-700">
                          Password protection: {passwordStatus ? "On" : "Off"}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-semibold text-gray-800">Password Protection</h4>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="password"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        placeholder={passwordStatus ? "Enter new password" : "Set password"}
                        className="w-full border rounded px-3 py-2"
                      />
                      <button
                        onClick={() => handleSavePasswordSetting(passwordInput)}
                        disabled={passwordSaving || !passwordInput.trim()}
                        className="bg-gray-800 text-white px-4 py-2 rounded disabled:opacity-50"
                      >
                        {passwordSaving ? "Saving..." : passwordStatus ? "Update" : "Set"}
                      </button>
                      {passwordStatus && (
                        <button
                          onClick={() => {
                            setPasswordInput("");
                            handleSavePasswordSetting("");
                          }}
                          disabled={passwordSaving}
                          className="bg-gray-200 text-gray-800 px-4 py-2 rounded"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-gray-700">
                      {failedAttempts.total > 0 ? (
                        <span>
                          Failed attempts: {failedAttempts.total}
                          {failedAttempts.last_attempt_at
                            ? ` • Last: ${new Date(
                                failedAttempts.last_attempt_at
                              ).toLocaleString()}`
                            : ""}
                        </span>
                      ) : (
                        <span>No failed password attempts yet.</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "cover" && (
                <div className="mt-2 space-y-3">
                  <h2 className="text-lg font-semibold mb-2">Set Cover Image</h2>
                  <div className="flex overflow-x-auto space-x-3 pb-1">
                    {images.map((img) => (
                      <div
                        key={img.id}
                        className="flex flex-col items-center gap-1"
                      >
                        <img
                          src={`https://imagedelivery.net/Z1o5jP3Z0wrLOM-5XDvR8A/${img.id}/thumbnail`}
                          alt="Cover option"
                          className={`w-16 h-16 object-cover cursor-pointer rounded border-2 ${
                            gallery.cover_image === img.id
                              ? "border-blue-600"
                              : "border-transparent hover:border-white"
                          }`}
                          onClick={() => handleSetCover(img.id)}
                        />
                        <button
                          onClick={() => handleDeleteImage(img.id)}
                          disabled={deletingImageId === img.id}
                          className="text-xs text-red-600 hover:underline disabled:opacity-60 relative z-10"
                        >
                          {deletingImageId === img.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "featured" && (
                <div className="mt-2 space-y-3">
                  <div>
                    <h2 className="text-lg font-semibold">Featured Photos</h2>
                    <p className="text-sm text-gray-600">
                      Star only the images you want in this category’s public slideshow. Stars from private or password-protected galleries remain saved but are never shown publicly.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-6">
                    {images.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => handleToggleFeatured(img.id)}
                        className={`relative aspect-square overflow-hidden border-2 ${
                          img.is_featured ? "border-amber-400" : "border-transparent"
                        }`}
                        aria-label={`${img.is_featured ? "Remove" : "Add"} photo ${img.id} ${img.is_featured ? "from" : "to"} featured photos`}
                        aria-pressed={Boolean(img.is_featured)}
                      >
                        <img src={img.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                        <span className={`absolute right-2 top-2 text-2xl drop-shadow ${
                          img.is_featured ? "text-amber-300" : "text-white/80"
                        }`} aria-hidden="true">★</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="relative w-full h-96 mb-6 mt-6">
            <img
              src={
                gallery.cover_image
                  ? `https://imagedelivery.net/Z1o5jP3Z0wrLOM-5XDvR8A/${gallery.cover_image}/thumbnail`
                  : images[0] &&
                    `https://imagedelivery.net/Z1o5jP3Z0wrLOM-5XDvR8A/${images[0].id}/thumbnail`
              }
              alt="Gallery Cover"
              className="object-cover w-full h-full"
            />
            <div className="absolute inset-0 bg-black bg-opacity-25 backdrop-blur-md flex flex-col items-center justify-center px-4">
              <h1 className="text-white text-3xl sm:text-5xl font-extrabold">
                {gallery.title}
              </h1>
              <p className="text-white text-sm sm:text-base mt-2">
                {gallery.description}
              </p>
            </div>
          </div>

          <div className="mt-6" ref={galleryGridRef}>
            <div style={{ height: topSpacerHeight }} aria-hidden="true" />
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}
            >
              {visibleImages.map((image, visibleIndex) => (
                <div
                  key={image.id}
                  className="mb-3 break-inside-avoid overflow-hidden group relative"
                >
                  {isGalleryOwner && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image.id);
                      }}
                      disabled={deletingImageId === image.id}
                      className="absolute top-2 right-2 z-10 bg-red-600 text-white text-xs px-3 py-1 rounded shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60"
                    >
                      {deletingImageId === image.id ? "Deleting..." : "Delete"}
                    </button>
                  )}
                    <img
                    src={image.thumbnailUrl}
                    alt={`Gallery Img ${image.id}`}
                    className="w-full h-[320px] object-cover transition-transform duration-300 transform group-hover:scale-105 cursor-pointer"
                    onClick={() => openLightbox(startIndex + visibleIndex)}
                  />
                </div>
              ))}
            </div>
            <div style={{ height: bottomSpacerHeight }} aria-hidden="true" />
          </div>

          {hasMoreImages && (
            <div className="mt-6 text-center">
              <button
                onClick={() => fetchGalleryImages(undefined, nextImageCursor)}
                disabled={loadingMoreImages}
                className="border border-gray-400 px-5 py-2 text-sm disabled:opacity-50"
              >
                {loadingMoreImages ? "Loading…" : "Load more photos"}
              </button>
            </div>
          )}

          {lightboxOpen && (
            <Lightbox
              open={lightboxOpen}
              close={() => setLightboxOpen(false)}
              slides={images.map((image) => ({
                src: image.fullSizeUrl,
                id: image.id,
              }))}
              index={lightboxIndex}
              controller={{ closeOnBackdropClick: true }}
              on={{ view: ({ index }) => setLightboxIndex(index) }}
              render={{
                buttonPrev: undefined,
                buttonNext: undefined,
                slideFooter: ({ slide }) =>
                  gallery?.allow_downloads ? (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-50 mt-4">
                      <button
                        onClick={() => {
                          const imageId = slide?.id || slide?.src?.split("/")[4];
                          if (imageId) handleShare(imageId);
                        }}
                        className="bg-gray-900 text-white px-4 py-2 rounded hover:bg-black text-sm shadow"
                      >
                        Share
                      </button>
                    </div>
                  ) : null,
              }}
            />
          )}
        </>
      ) : (
        <p className="text-center text-sm sm:text-base">Loading gallery...</p>
      )}
    </div>
  );
};

export default GalleryDetail;
